import React from "react";
import { clamp, ClassNameProps, makeClassName, Row, StyleProps, toGrammaticalNumber } from "@hex-insights/core";
import { Icon } from "../Icon";
import styles from "./styles.module.css";

export const statisticColors = {
	viewsColor: "var(--primary-color--disabled)",
	reactionsColor: "var(--theme-colors__green)",
	commentsColor: "var(--theme-colors__orange)",

	responseGoingColor: "var(--theme-colors__green)",
	responseNotGoingColor: "var(--theme-colors__orange)",
	responseMaybeColor: "var(--theme-colors__yellow)",
};

export type StatisticWithBarProps = {
	label: string;
	value: number;
	maxValue: number;
	IconComponent: (props: Icon.IconProps) => JSX.Element | null;
	color: string;
	noGrammaticalNumber?: boolean;
	barClassName?: string;
	barStyle?: React.CSSProperties;
	statisticClassName?: string;
	statisticStyle?: React.CSSProperties;
};

export function StatisticWithBar({
	label,
	value,
	maxValue,
	IconComponent,
	color,
	noGrammaticalNumber,
	barClassName,
	barStyle,
	statisticClassName,
	statisticStyle,
}: StatisticWithBarProps) {
	return (
		<React.Fragment>
			<StatisticBar value={value / maxValue} color={color} className={barClassName} style={barStyle} />

			<Statistic
				label={label}
				value={value}
				color={color}
				IconComponent={IconComponent}
				noGrammaticalNumber={noGrammaticalNumber}
				className={statisticClassName}
				style={statisticStyle}
			/>
		</React.Fragment>
	);
}

export type StatisticProps = {
	label: string;
	value: number;
	IconComponent: (props: Icon.IconProps) => JSX.Element | null;
	color: string;
	noGrammaticalNumber?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function Statistic({
	label,
	value,
	IconComponent,
	color,
	noGrammaticalNumber = false,
	className,
	style,
}: StatisticProps) {
	return (
		<Row
			justify="spaced-start"
			horizontalSpacing="0.5rem"
			align="center"
			className={makeClassName(styles["statistic"], className)}
			style={{ "--statistic-color": color, ...style } as React.CSSProperties}
		>
			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.375rem"
				className={styles["statistic__icon-value"]}
			>
				{IconComponent && <IconComponent />}
				<span>{value}</span>
			</Row>
			<span>{noGrammaticalNumber ? label : toGrammaticalNumber(label, value)}</span>
		</Row>
	);
}

export type StatisticBarProps = {
	value: number | null | undefined;
	color: string;
} & Partial<ClassNameProps & StyleProps>;

export function StatisticBar({ value, color, className, style }: StatisticBarProps) {
	const resolvedValue = isNonFormattableNumber(value) ? 0 : clamp(0, value, 1);

	return (
		<div
			className={makeClassName(styles["statistic-bar"], className)}
			style={{ "--bar-color": color, "--bar-value": resolvedValue, ...style } as React.CSSProperties}
		></div>
	);
}

function isNonFormattableNumber(value: number | null | undefined): value is null | undefined {
	return value === null || value === undefined || Number.isNaN(value);
}
